import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  Tabs,
  Tab,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Pagination,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Phone as PhoneIcon, 
  Email as EmailIcon, 
  LinkedIn as LinkedInIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  DateRange as DateRangeIcon,
  TrendingUp as TrendingUpIcon,
  KeyboardDoubleArrowUp as DoubleArrowUpIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { format, parseISO, startOfMonth, endOfMonth, subMonths, isWithinInterval, 
  startOfWeek, endOfWeek, subWeeks, addDays, eachDayOfInterval } from 'date-fns';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../api';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext'; // Import the auth context

const OutreachStatistics = () => {
  // State variables
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [outreaches, setOutreaches] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [userStatistics, setUserStatistics] = useState({});
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    username: '',
    method: '',
    dateRange: 'thisMonth',
    chartDateRange: 'thisMonth',
    reached: '',
    scheduled: ''
  });
  const [appliedFilters, setAppliedFilters] = useState({
    username: '',
    method: '',
    dateRange: 'thisMonth',
    chartDateRange: 'thisMonth',
    reached: '',
    scheduled: ''
  });
  const [selectedOutreach, setSelectedOutreach] = useState(null);
  const [outreachDialogOpen, setOutreachDialogOpen] = useState(false);
  const [companyNames, setCompanyNames] = useState({});
  const { isAdmin, isSupervisor, user } = useAuth(); // Get auth context
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamChecked, setTeamChecked] = useState(false);

  // Constants
  const recordsPerPage = 20;
  // Extended color palette with 20 distinct colors for better user differentiation
  const COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', // Original colors
    '#8884d8', '#82ca9d', '#ffc658', '#a4de6c', // Additional colors
    '#d0ed57', '#83a6ed', '#8dd1e1', '#5C7BD9', 
    '#FF6E5A', '#8A2BE2', '#FF7F50', '#20B2AA',
    '#FF69B4', '#6495ED', '#9ACD32', '#00CED1'
  ];
  
  // Function to get a consistent color for a username
  const getUserColor = (username) => {
    // Create a hash of the username to get a consistent index
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Make sure the hash is positive
    hash = Math.abs(hash);
    // Use modulo to get an index in the COLORS array
    return COLORS[hash % COLORS.length];
  };
  
  // Load data on component mount
  useEffect(() => {
    const initialLoad = async () => {
      // First load the basic data
      await fetchAllData();
      
      // Then apply the filters to show the default "thisMonth" data
      // We're using the filteredOutreaches memoized value which already applies the filters
      logger.log('Initial load complete, filters applied with dateRange:', appliedFilters.dateRange);
    };
    
    initialLoad();
  }, [isAdmin, isSupervisor]);

  // Update filtered data when chartDateRange changes
  useEffect(() => {
    if (outreaches.length > 0) {
      logger.log(`Chart date range changed to: ${appliedFilters.chartDateRange}, updating chart data...`);
      // No need to fetch data again, the useMemo hooks will recalculate
      // Just forcing a re-render to ensure charts update
      setLoading(true);
      setTimeout(() => setLoading(false), 0);
    }
  }, [appliedFilters.chartDateRange]);

  // Fetch all required data
  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Prepare date range for "thisMonth"
      const now = new Date();
      const startDate = startOfMonth(now);
      const endDate = endOfMonth(now);
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');

      let teamMembersForStats = [];
      try {
        const teamResponse = await api.getMyTeam();
        if (teamResponse?.success && teamResponse.team) {
          teamMembersForStats = teamResponse.members || teamResponse.team.members || [];
        }
      } catch (err) {
        logger.error('Error loading team for outreach statistics:', err);
      }

      const mergeTeamMembers = (people) => {
        const byUsername = new Map();
        [...people, ...teamMembersForStats].forEach((person) => {
          if (person?.username) {
            byUsername.set(person.username, person);
          }
        });
        return Array.from(byUsername.values());
      };
      
      // Only fetch users and user statistics if the user is an admin or supervisor
      if (isAdmin) {
        try {
          // Get all users for filtering
          const usersResponse = await api.getUsers();
          if (usersResponse.success) {
            const usersWithTeam = mergeTeamMembers(usersResponse.users);
            setUsers(usersWithTeam);
            // Get per-user statistics
            const userStats = {};
            await Promise.all(
              usersWithTeam.map(async (user) => {
                try {
                  const response = await api.getUserOutreachStatistics(user.username, startDateStr, endDateStr);
                  if (response.success) {
                    userStats[user.username] = response.data;
                  }
                } catch (err) {
                  logger.error(`Error getting stats for ${user.username}:`, err);
                }
              })
            );
            setUserStatistics(userStats);
          }
        } catch (err) {
          logger.error('Error loading user data:', err);
          // Continue with other data loading
        }
      } else if (isSupervisor) {
        try {
          // Get only assigned users for this supervisor
          const assignedResponse = await api.getAssignedUsers();
          if (assignedResponse.success) {
            // Include the supervisor themselves in the users list
            const usersWithSupervisor = [...assignedResponse.users];
            // Check if supervisor is already in the list to avoid duplicates
            const supervisorInUsers = usersWithSupervisor.find(u => u.username === user?.username);
            if (!supervisorInUsers && user?.username) {
              usersWithSupervisor.push({
                username: user.username,
                email: user.email || '',
                id: user.id || user.username
              });
            }
            const usersWithTeam = mergeTeamMembers(usersWithSupervisor);
            setUsers(usersWithTeam);
            
            // Get per-user statistics for assigned users AND the supervisor
            const userStats = {};
            await Promise.all(
              usersWithTeam.map(async (userItem) => {
                try {
                  const response = await api.getUserOutreachStatistics(userItem.username, startDateStr, endDateStr);
                  if (response.success) {
                    userStats[userItem.username] = response.data;
                  }
                } catch (err) {
                  logger.error(`Error getting stats for ${userItem.username}:`, err);
                }
              })
            );
            setUserStatistics(userStats);
          } else {
            const supervisorUser = user?.username ? [{
              username: user.username,
              email: user.email || '',
              id: user.id || user.username
            }] : [];
            const people = mergeTeamMembers(supervisorUser);
            setUsers(people);
            const userStats = {};
            await Promise.all(
              people.map(async (person) => {
                try {
                  const response = await api.getUserOutreachStatistics(person.username, startDateStr, endDateStr);
                  if (response.success) {
                    userStats[person.username] = response.data;
                  }
                } catch (err) {
                  logger.error(`Error getting stats for ${person.username}:`, err);
                }
              })
            );
            setUserStatistics(userStats);
          }
        } catch (err) {
          logger.error('Error loading assigned user data:', err);
          if (user?.username) {
            const people = mergeTeamMembers([{
              username: user.username,
              email: user.email || '',
              id: user.id || user.username
            }]);
            setUsers(people);
            const userStats = {};
            await Promise.all(
              people.map(async (person) => {
                try {
                  const response = await api.getUserOutreachStatistics(person.username, startDateStr, endDateStr);
                  if (response.success) {
                    userStats[person.username] = response.data;
                  }
                } catch (statErr) {
                  logger.error(`Error getting fallback stats for ${person.username}:`, statErr);
                }
              })
            );
            setUserStatistics(userStats);
          }
        }
      } else if (user) {
        const people = mergeTeamMembers([user]);
        setUsers(people);
        try {
          const userStats = {};
          await Promise.all(
            people.map(async (person) => {
              const response = await api.getUserOutreachStatistics(person.username, startDateStr, endDateStr);
              if (response.success) {
                userStats[person.username] = response.data;
              }
            })
          );
          setUserStatistics(userStats);
        } catch (err) {
          logger.error(`Error getting stats for ${user.username}:`, err);
        }
      }

      // Get global outreach statistics
      try {
        const statsResponse = await api.getOutreachStatistics(startDateStr, endDateStr);
        if (statsResponse.success) {
          setStatistics(statsResponse.data);
        }
      } catch (err) {
        logger.error('Error loading outreach statistics:', err);
        // Initialize with empty statistics rather than failing completely
        setStatistics({
          total_outreaches: 0,
          by_method: {},
          successful_contacts: 0,
          meetings_scheduled: 0,
          success_rate: 0,
          meeting_conversion_rate: 0,
          current_streak: 0
        });
      }

      // Get all outreach records
      try {
        // Set appropriate view mode based on user role
        const viewMode = isAdmin || isSupervisor || teamMembersForStats.length > 0 ? 'all' : 'user_only';
        
        const outreachResponse = await api.getOutreaches(startDateStr, endDateStr, null, viewMode);
        if (outreachResponse.success) {
          setOutreaches(outreachResponse.data || []);
          setTotalPages(Math.ceil((outreachResponse.data?.length || 0) / recordsPerPage));
        }
      } catch (err) {
        logger.error('Error loading outreach records:', err);
        setOutreaches([]);
        setTotalPages(1);
      }
    } catch (err) {
      logger.error('Error loading outreach statistics:', err);
      setError('Failed to load outreach data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch additional data to map record_ids to company names
  useEffect(() => {
    const fetchCompanyNames = async () => {
      if (outreaches.length === 0) return;
      
      try {
        // Get unique record IDs
        const recordIds = [...new Set(outreaches.map(o => o.record_id))];
        
        // Create a mapping to store company names
        const companyData = {};
        
        // First, try to extract company names from outreaches if they have it
        outreaches.forEach(outreach => {
          if (outreach.company_name) {
            companyData[outreach.record_id] = outreach.company_name;
          }
        });
        
        // Process in batches of 10 to avoid too many concurrent requests
        const batchSize = 10;
        for (let i = 0; i < recordIds.length; i += batchSize) {
          const batch = recordIds.slice(i, i + batchSize);
          
          // Process batch in parallel
          await Promise.all(
            batch.map(async (recordId) => {
              // Skip if we already have the company name
              if (companyData[recordId]) return;
              
              try {
                const response = await api.checkOutreachStatus(recordId);
                if (response.success) {
                  if (response.company_name) {
                    companyData[recordId] = response.company_name;
                  }
                }
              } catch (err) {
                logger.error(`Error getting company info for ${recordId}:`, err);
              }
            })
          );
        }
        
        setCompanyNames(companyData);
      } catch (err) {
        logger.error('Error fetching company names:', err);
      }
    };
    
    fetchCompanyNames();
  }, [outreaches]);

  // Fetch team members for supervisor on mount
  useEffect(() => {
    const fetchTeamMembers = async () => {
      if (isSupervisor) {
        try {
          const response = await api.getAssignedUsers();
          if (response.success && Array.isArray(response.users)) {
            // Include the supervisor themselves in the team members list
            const teamWithSupervisor = [...response.users];
            // Check if supervisor is already in the list to avoid duplicates
            const supervisorInTeam = teamWithSupervisor.find(member => member.username === user?.username);
            if (!supervisorInTeam && user?.username) {
              teamWithSupervisor.push({
                username: user.username,
                email: user.email || '',
                id: user.id || user.username
              });
            }
            setTeamMembers(teamWithSupervisor);
          } else {
            // If no team members, at least include the supervisor themselves
            setTeamMembers(user?.username ? [{
              username: user.username,
              email: user.email || '',
              id: user.id || user.username
            }] : []);
          }
        } catch (err) {
          // If error fetching team, at least include the supervisor themselves
          setTeamMembers(user?.username ? [{
            username: user.username,
            email: user.email || '',
            id: user.id || user.username
          }] : []);
        } finally {
          setTeamChecked(true);
        }
      } else {
        setTeamChecked(true);
      }
    };
    fetchTeamMembers();
  }, [isSupervisor, user]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    logger.log(`Filter changed: ${name} = ${value}`);
    
    const newFilters = {
      ...filters,
      [name]: value
    };
    
    // Update the filters state
    setFilters(newFilters);
    
    // If we're changing the chartDateRange, we need to update the UI immediately
    if (name === 'chartDateRange') {
      // Only update the chartDateRange in appliedFilters
      setAppliedFilters(prev => ({
        ...prev,
        chartDateRange: value
      }));
    } else {
      // For other filters, apply them the normal way
      setAppliedFilters(newFilters);
      setCurrentPage(1);
    }
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      username: '',
      method: '',
      dateRange: 'thisMonth',
      chartDateRange: 'thisMonth',
      reached: '',
      scheduled: ''
    });
    setAppliedFilters({
      username: '',
      method: '',
      dateRange: 'thisMonth',
      chartDateRange: 'thisMonth',
      reached: '',
      scheduled: ''
    });
    setCurrentPage(1);
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };
  
  // Filter outreaches based on applied filters
  const filteredOutreaches = useMemo(() => {
    let result = [...outreaches];
    
    if (appliedFilters.username) {
      result = result.filter(item => item.username === appliedFilters.username);
    }
    
    if (appliedFilters.method) {
      result = result.filter(item => item.outreach_method === appliedFilters.method);
    }
    
    if (appliedFilters.reached !== '') {
      const reachedValue = appliedFilters.reached === 'true';
      result = result.filter(item => item.reached_intended_contact === reachedValue);
    }
    
    if (appliedFilters.scheduled !== '') {
      const scheduledValue = appliedFilters.scheduled === 'true';
      result = result.filter(item => item.meeting_scheduled === scheduledValue);
    }
    
    if (appliedFilters.dateRange !== 'all') {
      const now = new Date();
      let startDate, endDate;
      
      switch (appliedFilters.dateRange) {
        case 'thisWeek':
          // Week starts on Monday (weekStartsOn: 1)
          startDate = startOfWeek(now, { weekStartsOn: 1 });
          endDate = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'lastWeek':
          // Last week with week start on Monday
          startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
          endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
          break;
        case 'thisMonth':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        case 'lastMonth':
          startDate = startOfMonth(subMonths(now, 1));
          endDate = endOfMonth(subMonths(now, 1));
          break;
        case 'last3Months':
          // Start from 2 months ago (current month + 2 previous months = 3 months)
          startDate = startOfMonth(subMonths(now, 2));
          endDate = endOfMonth(now);
          break;
        default:
          // If not a recognized value, default to all time
          startDate = new Date(0); // Earliest possible date
          endDate = now;
      }
      
      // Ensure startDate has time set to start of day
      startDate.setHours(0, 0, 0, 0);
      
      // Ensure endDate has time set to end of day
      endDate.setHours(23, 59, 59, 999);
      
      result = result.filter(item => {
        if (!item.outreach_date) return false;
        
        try {
          const outreachDate = parseISO(item.outreach_date);
          return isWithinInterval(outreachDate, { start: startDate, end: endDate });
        } catch (err) {
          logger.error(`Error parsing outreach date: ${item.outreach_date}`, err);
          return false;
        }
      });
    }
    
    return result;
  }, [outreaches, appliedFilters]);
  
  // Paginate the filtered outreaches
  const paginatedOutreaches = useMemo(() => {
    const startIndex = (currentPage - 1) * recordsPerPage;
    const endIndex = startIndex + recordsPerPage;
    return filteredOutreaches.slice(startIndex, endIndex);
  }, [filteredOutreaches, currentPage]);

  // Prepare data for charts
  const methodChartData = useMemo(() => {
    if (!statistics) return [];
    return Object.entries(statistics.by_method || {}).map(([name, value]) => ({
      name,
      value
    }));
  }, [statistics]);

  const userPerformanceData = useMemo(() => {
    return Object.entries(userStatistics).map(([username, stats]) => ({
      name: username,
      total: stats.total_outreaches || 0,
      successful: stats.successful_contacts || 0,
      meetings: stats.meetings_scheduled || 0,
      successRate: stats.success_rate || 0
    }));
  }, [userStatistics]);
  
  // Filtered user performance data based on date range
  const filteredUserPerformanceData = useMemo(() => {
    // When "all" is selected, we should use the original aggregated stats from the backend
    if (appliedFilters.chartDateRange === 'all') {
      return userPerformanceData;
    }
    
    // Filter outreaches by the selected date range
    const now = new Date();
    let startDate, endDate;
    
    switch (appliedFilters.chartDateRange) {
      case 'thisWeek':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'lastMonth':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last3Months':
        startDate = startOfMonth(subMonths(now, 2));
        endDate = endOfMonth(now);
        break;
      default:
        return userPerformanceData; // Use original data for any other value
    }
    
    // Filter outreaches by date
    const filteredOutreaches = outreaches.filter(outreach => {
      const outreachDate = new Date(outreach.outreach_date);
      return isWithinInterval(outreachDate, { start: startDate, end: endDate });
    });
    
    // Create a map of username -> stats
    const userStats = {};
    
    // Initialize stats for all users
    users.forEach(user => {
      userStats[user.username] = {
        total: 0,
        successful: 0,
        meetings: 0
      };
    });
    
    // Calculate stats from filtered outreaches
    filteredOutreaches.forEach(outreach => {
      const username = outreach.username;
      if (userStats[username]) {
        userStats[username].total++;
        
        if (outreach.reached_intended_contact) {
          userStats[username].successful++;
        }
        
        if (outreach.meeting_scheduled) {
          userStats[username].meetings++;
        }
      }
    });
    
    // Format data for chart
    return Object.entries(userStats).map(([username, stats]) => {
      const successRate = stats.total > 0 ? (stats.successful / stats.total * 100) : 0;
      return {
        name: username,
        total: stats.total,
        successful: stats.successful,
        meetings: stats.meetings,
        successRate: Math.round(successRate * 100) / 100 // Round to 2 decimal places
      };
    });
  }, [outreaches, users, appliedFilters.chartDateRange, userPerformanceData]);

  // Prepare day-to-day data for charts
  const prepareDailyUserData = useMemo(() => {
    // Apply the same date range filter as used in filteredOutreaches
    let filteredData = [...outreaches];
    const now = new Date();
    let startDate, endDate;
    
    if (appliedFilters.chartDateRange !== 'all') {
      switch (appliedFilters.chartDateRange) {
        case 'thisWeek':
          startDate = startOfWeek(now, { weekStartsOn: 1 });
          endDate = endOfWeek(now, { weekStartsOn: 1 });
          break;
        case 'lastWeek':
          startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
          endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
          break;
        case 'thisMonth':
          startDate = startOfMonth(now);
          endDate = endOfMonth(now);
          break;
        case 'lastMonth':
          startDate = startOfMonth(subMonths(now, 1));
          endDate = endOfMonth(subMonths(now, 1));
          break;
        case 'last3Months':
          startDate = startOfMonth(subMonths(now, 2));
          endDate = endOfMonth(now);
          break;
        default:
          // For "all", limit to last 30 days to keep the chart readable
          startDate = subMonths(now, 1);
          endDate = now;
      }
    } else {
      // For "all", limit to last 30 days to keep the chart readable
      startDate = subMonths(now, 1);
      endDate = now;
    }
    
    // Generate all dates in the interval
    const dateRange = eachDayOfInterval({ start: startDate, end: endDate });
    
    // Create a map to count outreaches by day and user
    const dailyData = {};
    
    // Initialize the data structure for all users and all days
    dateRange.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      dailyData[dateStr] = { date: dateStr };
      
      // Add each user with 0 count
      users.forEach(user => {
        dailyData[dateStr][user.username] = 0;
      });
    });
    
    // Count outreaches for each user on each day
    filteredData.forEach(outreach => {
      const outreachDate = new Date(outreach.outreach_date);
      
      // Only count if within the date range
      if (isWithinInterval(outreachDate, { start: startDate, end: endDate })) {
        const dateStr = format(outreachDate, 'yyyy-MM-dd');
        const username = outreach.username;
        
        if (dailyData[dateStr] && username) {
          if (dailyData[dateStr][username] === undefined) {
            dailyData[dateStr][username] = 1;
          } else {
            dailyData[dateStr][username]++;
          }
        }
      }
    });
    
    // Convert to array for the chart
    return Object.values(dailyData).sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });
  }, [outreaches, appliedFilters.chartDateRange, users]);

  // Filter the statistics and chart data based on date range
  const filteredChartData = useMemo(() => {
    logger.log(`Recalculating method chart data for date range: ${appliedFilters.chartDateRange}`);
    
    // When "all" is selected, use the original statistics from the backend
    if (!statistics || appliedFilters.chartDateRange === 'all') {
      return methodChartData;
    }
    
    // For specific date ranges, calculate from filtered outreaches
    const now = new Date();
    let startDate, endDate;
    
    switch (appliedFilters.chartDateRange) {
      case 'thisWeek':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'lastMonth':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last3Months':
        startDate = startOfMonth(subMonths(now, 2));
        endDate = endOfMonth(now);
        break;
      default:
        return methodChartData; // Use original data for any other value
    }
    
    // Filter outreaches by date range
    const filteredOutreaches = outreaches.filter(item => {
      const outreachDate = new Date(item.outreach_date);
      return isWithinInterval(outreachDate, { start: startDate, end: endDate });
    });
    
    logger.log(`Found ${filteredOutreaches.length} outreaches in date range ${format(startDate, 'yyyy-MM-dd')} to ${format(endDate, 'yyyy-MM-dd')}`);
    
    // Create method counts
    const methodCounts = {};
    filteredOutreaches.forEach(outreach => {
      const method = outreach.outreach_method || 'unknown';
      methodCounts[method] = (methodCounts[method] || 0) + 1;
    });
    
    // Prepare chart data
    return Object.entries(methodCounts).map(([name, value]) => ({
      name,
      value
    }));
  }, [outreaches, methodChartData, appliedFilters.chartDateRange, statistics]);

  // Calculate filtered statistics for the cards based on the selected date range
  const filteredCardStats = useMemo(() => {
    // When "all" is selected, use the original statistics from the backend
    if (!statistics || appliedFilters.chartDateRange === 'all') {
      return statistics;
    }
    
    // For specific date ranges, calculate from filtered outreaches
    const now = new Date();
    let startDate, endDate;
    
    switch (appliedFilters.chartDateRange) {
      case 'thisWeek':
        startDate = startOfWeek(now, { weekStartsOn: 1 });
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'lastWeek':
        startDate = startOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        endDate = endOfWeek(subWeeks(now, 1), { weekStartsOn: 1 });
        break;
      case 'thisMonth':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'lastMonth':
        startDate = startOfMonth(subMonths(now, 1));
        endDate = endOfMonth(subMonths(now, 1));
        break;
      case 'last3Months':
        startDate = startOfMonth(subMonths(now, 2));
        endDate = endOfMonth(now);
        break;
      default:
        return statistics; // Use original data for any other value
    }
    
    // Filter outreaches by date range
    const filteredOutreaches = outreaches.filter(item => {
      const outreachDate = new Date(item.outreach_date);
      return isWithinInterval(outreachDate, { start: startDate, end: endDate });
    });
    
    // Calculate stats from filtered outreaches
    const totalOutreaches = filteredOutreaches.length;
    const successfulContacts = filteredOutreaches.filter(o => o.reached_intended_contact).length;
    const meetingsScheduled = filteredOutreaches.filter(o => o.meeting_scheduled).length;
    
    // Calculate percentages
    const successRate = totalOutreaches > 0 ? Math.round(successfulContacts / totalOutreaches * 100) : 0;
    const conversionRate = successfulContacts > 0 ? Math.round(meetingsScheduled / successfulContacts * 100) : 0;
    
    // Create filtered stats object with same structure as the original
    return {
      total_outreaches: totalOutreaches,
      successful_contacts: successfulContacts,
      meetings_scheduled: meetingsScheduled,
      success_rate: successRate,
      meeting_conversion_rate: conversionRate,
      // Keep the same streak as the original (streak isn't affected by date filtering)
      current_streak: statistics.current_streak
    };
  }, [outreaches, statistics, appliedFilters.chartDateRange]);

  // Helper function to render method icon
  const renderMethodIcon = (method) => {
    switch (method) {
      case 'phone':
        return <PhoneIcon fontSize="small" color="primary" />;
      case 'email':
        return <EmailIcon fontSize="small" color="primary" />;
      case 'linkedin':
        return <LinkedInIcon fontSize="small" color="primary" />;
      default:
        return null;
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch (err) {
      return dateString;
    }
  };

  // Function to handle opening outreach detail dialog
  const handleViewOutreachDetails = (outreach) => {
    // Store the selected outreach data
    setSelectedOutreach(outreach);
    setOutreachDialogOpen(true);
    
    // No need to make an additional API call here - we already have the outreach data
    // The outreach details dialog just displays the data from the selectedOutreach state
  };
  
  // Function to close outreach detail dialog
  const handleCloseOutreachDialog = () => {
    setOutreachDialogOpen(false);
    setSelectedOutreach(null);
  };

  // Add manual reload function
  const handleManualReload = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load fresh data
      await fetchAllData();
      
      // Apply the current filters (the filters will be applied automatically via useMemo)
      logger.log('Manual reload complete, filters applied with dateRange:', appliedFilters.dateRange);
    } catch (error) {
      logger.error('Error during manual reload:', error);
      setError('Failed to reload data: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  // If loading
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Loading outreach statistics...</Alert>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  // If error
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={handleManualReload}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Supervisor with no team notification */}
      {isSupervisor && teamChecked && teamMembers.length <= 1 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You currently have no team members assigned (only your own statistics are shown). Please contact an admin to request members to be added to your team.
        </Alert>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Outreach Statistics</Typography>
        <Button 
          variant="outlined" 
          startIcon={<RefreshIcon />} 
          onClick={handleManualReload}
          disabled={loading}
        >
          Reload Data
        </Button>
      </Box>

      <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="User Outreach Stats" />
        <Tab label="Outreach History" />
      </Tabs>

      {/* Overview Tab */}
      {tabValue === 0 && statistics && (
        <Grid container spacing={3}>
          {/* Date Filter for Overview */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                  <DateRangeIcon sx={{ mr: 1 }} /> 
                  Date Range
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Filter Charts</InputLabel>
                    <Select
                      name="chartDateRange"
                      value={filters.chartDateRange}
                      label="Filter Charts"
                      onChange={handleFilterChange}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="thisWeek">This Week</MenuItem>
                      <MenuItem value="lastWeek">Last Week</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="lastMonth">Last Month</MenuItem>
                      <MenuItem value="last3Months">Last 3 Months</MenuItem>
                    </Select>
                  </FormControl>
                  <Button 
                    variant="outlined" 
                    size="small"
                    onClick={resetFilters}
                    startIcon={<RefreshIcon />}
                  >
                    Reset
                  </Button>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Outreach Activities
                </Typography>
                <Typography variant="h4">
                  {filteredCardStats.total_outreaches}
                </Typography>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Successful Contacts
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4">
                    {filteredCardStats.successful_contacts}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                    ({filteredCardStats.success_rate}%)
                  </Typography>
                </Box>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Meetings Scheduled
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="h4">
                    {filteredCardStats.meetings_scheduled}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
                    ({filteredCardStats.meeting_conversion_rate}%)
                  </Typography>
                </Box>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Current Streak
                </Typography>
                <Typography variant="h4">
                  {filteredCardStats.current_streak} days
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Charts */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  Outreach Methods
                </Typography>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </Box>
              {filteredChartData && filteredChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="90%">
                  <PieChart>
                    <Pie
                      data={filteredChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {filteredChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <ChartTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    No data available for the selected date range
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={resetFilters}
                    startIcon={<DateRangeIcon />}
                  >
                    Reset Date Filter
                  </Button>
                </Box>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2, height: 400 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  User Outreach Performance
                </Typography>
                {appliedFilters.chartDateRange !== 'all' && (
                  <Typography variant="subtitle2" color="text.secondary">
                    {appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                     appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                     appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                     appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                     appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
                  </Typography>
                )}
              </Box>
              {filteredUserPerformanceData.length > 0 ? (
                <ResponsiveContainer width="100%" height="90%">
                  <BarChart 
                    data={filteredUserPerformanceData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={60} />
                    <YAxis />
                    <ChartTooltip />
                    <Legend />
                    <Bar dataKey="total" name="Total Outreaches">
                      {
                        filteredUserPerformanceData.map((entry, index) => (
                          <Cell key={`cell-total-${index}`} fill={getUserColor(entry.name)} />
                        ))
                      }
                    </Bar>
                    <Bar dataKey="successful" name="Successful">
                      {
                        filteredUserPerformanceData.map((entry, index) => (
                          <Cell key={`cell-successful-${index}`} fill={getUserColor(entry.name)} fillOpacity={0.7} />
                        ))
                      }
                    </Bar>
                    <Bar dataKey="meetings" name="Meetings">
                      {
                        filteredUserPerformanceData.map((entry, index) => (
                          <Cell key={`cell-meetings-${index}`} fill={getUserColor(entry.name)} fillOpacity={0.4} />
                        ))
                      }
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                  <Typography variant="body1" color="text.secondary">
                    No user performance data available
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* User Outreach Stats Tab */}
      {tabValue === 1 && (
        <>
          {/* Date Filter for User Outreach Stats */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                <DateRangeIcon sx={{ mr: 1 }} /> 
                Date Range
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Filter Charts</InputLabel>
                  <Select
                    name="chartDateRange"
                    value={filters.chartDateRange}
                    label="Filter Charts"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="thisMonth">This Month</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                    <MenuItem value="last3Months">Last 3 Months</MenuItem>
                  </Select>
                </FormControl>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={resetFilters}
                  startIcon={<RefreshIcon />}
                >
                  Reset
                </Button>
              </Box>
            </Box>
          </Paper>

          {/* Daily User Performance Chart */}
          <Paper sx={{ p: 2, mb: 3, height: 400 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Daily Outreach Activity by User
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                {appliedFilters.chartDateRange === 'all' ? 'Last 30 Days' : 
                 appliedFilters.chartDateRange === 'thisWeek' ? 'This Week' :
                 appliedFilters.chartDateRange === 'lastWeek' ? 'Last Week' :
                 appliedFilters.chartDateRange === 'thisMonth' ? 'This Month' :
                 appliedFilters.chartDateRange === 'lastMonth' ? 'Last Month' :
                 appliedFilters.chartDateRange === 'last3Months' ? 'Last 3 Months' : ''}
              </Typography>
            </Box>
            {prepareDailyUserData.length > 0 ? (
              <ResponsiveContainer width="100%" height="90%">
                <BarChart 
                  data={prepareDailyUserData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    angle={-45} 
                    textAnchor="end" 
                    height={60}
                    tickFormatter={(date) => format(new Date(date), 'MMM d')}
                  />
                  <YAxis />
                  <ChartTooltip 
                    formatter={(value, name) => [value, `User: ${name}`]}
                    labelFormatter={(date) => format(new Date(date), 'EEEE, MMMM d, yyyy')}
                  />
                  <Legend />
                  {users.map((user, index) => (
                    <Bar 
                      key={user.username} 
                      dataKey={user.username} 
                      name={user.username} 
                      stackId="a"
                      fill={getUserColor(user.username)} 
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80%' }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                  No data available for the selected date range
                </Typography>
                <Button 
                  variant="outlined" 
                  onClick={resetFilters}
                  startIcon={<DateRangeIcon />}
                >
                  Reset Date Filter
                </Button>
              </Box>
            )}
          </Paper>

          {/* Overall User Performance Stats Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell align="right">Total Outreaches</TableCell>
                  <TableCell align="right">Phone</TableCell>
                  <TableCell align="right">Email</TableCell>
                  <TableCell align="right">LinkedIn</TableCell>
                  <TableCell align="right">Success Rate</TableCell>
                  <TableCell align="right">Meetings</TableCell>
                  <TableCell align="right">Conversion Rate</TableCell>
                  <TableCell align="right">Streak</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(userStatistics).map(([username, stats]) => (
                  <TableRow key={username} hover>
                    <TableCell>{username}</TableCell>
                    <TableCell align="right">{stats.total_outreaches}</TableCell>
                    <TableCell align="right">{stats.by_method.phone || 0}</TableCell>
                    <TableCell align="right">{stats.by_method.email || 0}</TableCell>
                    <TableCell align="right">{stats.by_method.linkedin || 0}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${stats.success_rate}%`} 
                        color={stats.success_rate > 50 ? "success" : stats.success_rate > 30 ? "warning" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{stats.meetings_scheduled}</TableCell>
                    <TableCell align="right">
                      <Chip 
                        label={`${stats.meeting_conversion_rate}%`} 
                        color={stats.meeting_conversion_rate > 40 ? "success" : stats.meeting_conversion_rate > 20 ? "warning" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {stats.current_streak > 0 && (
                        <Tooltip title={`${stats.current_streak} day streak`}>
                          <Chip 
                            label={stats.current_streak} 
                            color="primary"
                            size="small" 
                            icon={<DateRangeIcon />}
                          />
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Outreach History Tab */}
      {tabValue === 2 && (
        <>
          {/* Filters */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1 }} /> 
              Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    name="username"
                    value={filters.username}
                    label="User"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Users</MenuItem>
                    {(isSupervisor ? teamMembers : users).map(user => (
                      <MenuItem key={user.username} value={user.username}>
                        {user.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Method</InputLabel>
                  <Select
                    name="method"
                    value={filters.method}
                    label="Method"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All Methods</MenuItem>
                    <MenuItem value="phone">Phone</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="linkedin">LinkedIn</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Date Range</InputLabel>
                  <Select
                    name="dateRange"
                    value={filters.dateRange}
                    label="Date Range"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="all">All Time</MenuItem>
                    <MenuItem value="thisWeek">This Week</MenuItem>
                    <MenuItem value="lastWeek">Last Week</MenuItem>
                    <MenuItem value="thisMonth">This Month</MenuItem>
                    <MenuItem value="lastMonth">Last Month</MenuItem>
                    <MenuItem value="last3Months">Last 3 Months</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Contact Reached</InputLabel>
                  <Select
                    name="reached"
                    value={filters.reached}
                    label="Contact Reached"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Meeting Scheduled</InputLabel>
                  <Select
                    name="scheduled"
                    value={filters.scheduled}
                    label="Meeting Scheduled"
                    onChange={handleFilterChange}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    onClick={resetFilters}
                    fullWidth
                    startIcon={<RefreshIcon />}
                  >
                    Reset Filters
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
          
          {/* Results count */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Showing {filteredOutreaches.length} outreach {filteredOutreaches.length === 1 ? 'record' : 'records'}
            </Typography>
          </Box>
          
          {/* Outreach history table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Reached?</TableCell>
                  <TableCell>Meeting</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedOutreaches.length > 0 ? (
                  paginatedOutreaches.map((outreach) => (
                    <TableRow 
                      key={outreach.id} 
                      hover
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                      }}
                      onClick={() => handleViewOutreachDetails(outreach)}
                    >
                      <TableCell>{formatDate(outreach.outreach_date)}</TableCell>
                      <TableCell>{outreach.username}</TableCell>
                      <TableCell>{outreach.contact_name}</TableCell>
                      <TableCell>
                        {companyNames[outreach.record_id] ? (
                          <Typography variant="body2" noWrap>
                            {companyNames[outreach.record_id]}
                          </Typography>
                        ) : (
                          <Tooltip title={`Record ID: ${outreach.record_id}`}>
                            <span>{outreach.record_id.substring(0, 8)}...</span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {renderMethodIcon(outreach.outreach_method)}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {outreach.outreach_method}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {outreach.reached_intended_contact ? (
                          <Chip 
                            icon={<CheckCircleIcon />} 
                            label="Yes" 
                            color="success" 
                            size="small" 
                          />
                        ) : (
                          <Chip 
                            icon={<CancelIcon />} 
                            label="No" 
                            color="error" 
                            size="small" 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {outreach.meeting_scheduled ? (
                          <Tooltip title={`Meeting on ${formatDate(outreach.meeting_date)}`}>
                            <Chip 
                              icon={<EventIcon />} 
                              label="Scheduled" 
                              color="primary" 
                              size="small" 
                            />
                          </Tooltip>
                        ) : (
                          outreach.followup_date ? (
                            <Tooltip title={`Follow-up on ${formatDate(outreach.followup_date)}`}>
                              <Chip 
                                icon={<ScheduleIcon />} 
                                label="Follow-up" 
                                color="info" 
                                size="small" 
                              />
                            </Tooltip>
                          ) : (
                            <Chip 
                              label="None" 
                              variant="outlined" 
                              size="small" 
                            />
                          )
                        )}
                      </TableCell>
                      <TableCell>
                        {outreach.notes && (
                          <Tooltip title={outreach.notes}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {outreach.notes.substring(0, 50)}
                              {outreach.notes.length > 50 ? '...' : ''}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewOutreachDetails(outreach);
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      No outreach records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredOutreaches.length > recordsPerPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination 
                count={Math.ceil(filteredOutreaches.length / recordsPerPage)} 
                page={currentPage} 
                onChange={handlePageChange} 
                color="primary" 
              />
            </Box>
          )}
        </>
      )}

      {/* Outreach Detail Dialog */}
      <Dialog
        open={outreachDialogOpen}
        onClose={handleCloseOutreachDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Outreach Details
          {selectedOutreach && companyNames[selectedOutreach.record_id] && (
            <>: {companyNames[selectedOutreach.record_id]}</>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selectedOutreach && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Contact Information</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1"><strong>Contact Name:</strong> {selectedOutreach.contact_name}</Typography>
                  <Typography variant="body1">
                    <strong>Contact Method:</strong> {selectedOutreach.outreach_method}
                    {' '}{renderMethodIcon(selectedOutreach.outreach_method)}
                  </Typography>
                  {selectedOutreach.contact_detail && (
                    <Typography variant="body1"><strong>Contact Detail:</strong> {selectedOutreach.contact_detail}</Typography>
                  )}
                  <Typography variant="body1">
                    <strong>Reached Contact:</strong> {selectedOutreach.reached_intended_contact ? 'Yes' : 'No'}
                  </Typography>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" gutterBottom>Dates & Status</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1">
                    <strong>Outreach Date:</strong> {formatDate(selectedOutreach.outreach_date)}
                  </Typography>
                  {selectedOutreach.meeting_scheduled && (
                    <Typography variant="body1">
                      <strong>Meeting Date:</strong> {formatDate(selectedOutreach.meeting_date)}
                    </Typography>
                  )}
                  {selectedOutreach.followup_date && (
                    <Typography variant="body1">
                      <strong>Follow-up Date:</strong> {formatDate(selectedOutreach.followup_date)}
                    </Typography>
                  )}
                  <Typography variant="body1">
                    <strong>Created:</strong> {formatDate(selectedOutreach.created_at)}
                  </Typography>
                  {selectedOutreach.updated_at !== selectedOutreach.created_at && (
                    <Typography variant="body1">
                      <strong>Last Updated:</strong> {formatDate(selectedOutreach.updated_at)}
                    </Typography>
                  )}
                </Paper>
              </Grid>
              
              {selectedOutreach.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Notes</Typography>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedOutreach.notes}
                    </Typography>
                  </Paper>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>Record Information</Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body1">
                    <strong>Record ID:</strong> {selectedOutreach.record_id}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Company:</strong> {companyNames[selectedOutreach.record_id] || 'N/A'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>By User:</strong> {selectedOutreach.username}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseOutreachDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutreachStatistics; 